import { computeAutoScrollDelta } from './geometry'

/** An ancestor the user can scroll, and the axes it allows scrolling on. */
export type ScrollParent = {
    element: HTMLElement
    scrollsX: boolean
    scrollsY: boolean
}

// `overflow: hidden` and `clip` boxes can still be scrolled from code, so
// overflowing content alone does not make an ancestor a scroll container.
function isUserScrollable(overflow: string): boolean {
    return overflow === 'auto' || overflow === 'scroll'
}

export function collectScrollParents(element: HTMLElement): ScrollParent[] {
    // Snapshot once at drag start: recomputing the ancestor chain every frame
    // would cost layout reads per frame for a list that rarely changes.
    const parents: ScrollParent[] = []
    let node = element.parentElement
    while (node !== null) {
        const style = getComputedStyle(node)
        const scrollsX = isUserScrollable(style.overflowX) && node.scrollWidth > node.clientWidth
        const scrollsY = isUserScrollable(style.overflowY) && node.scrollHeight > node.clientHeight
        if (scrollsX || scrollsY) parents.push({ element: node, scrollsX, scrollsY })
        node = node.parentElement
    }
    return parents
}

function scrollContainer({ element, scrollsX, scrollsY }: ScrollParent, x: number, y: number): void {
    // The tree may have changed mid-drag; detached containers are simply skipped.
    if (!element.isConnected) return
    const rect = element.getBoundingClientRect()
    // The edge bands sit inside the container. A pointer past them is over
    // some other part of the page, such as a drop target beside a table.
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return
    const deltaX = scrollsX ? computeAutoScrollDelta(x, rect.left, rect.right) : 0
    const deltaY = scrollsY ? computeAutoScrollDelta(y, rect.top, rect.bottom) : 0
    if (deltaX !== 0) element.scrollLeft += deltaX
    if (deltaY !== 0) element.scrollTop += deltaY
}

export function autoScroll(scrollParents: ScrollParent[], x: number, y: number): void {
    // Window edges first so page-level scrolling always works, then containers.
    const windowDeltaX = computeAutoScrollDelta(x, 0, window.innerWidth)
    const windowDeltaY = computeAutoScrollDelta(y, 0, window.innerHeight)
    if (windowDeltaX !== 0 || windowDeltaY !== 0) window.scrollBy(windowDeltaX, windowDeltaY)
    for (const container of scrollParents) scrollContainer(container, x, y)
}
